/**
 * `disclosure` — the host-agnostic **command-builder + discriminant model** behind the client's
 * disclosure surfaces: `DisclosureControl`, the `showEnemyHp` toggle, and the masked-render
 * branches in `CombatWindows.tsx`. Kept pure so "the control issues X command" / "a masked
 * combatant hides its stats" is provable by calling the exact function the component calls —
 * this repo has no jsdom/headless React renderer. Same pattern as `tracks.ts`'s `setTrackCommand`
 * and `combatWindowsChoosers.ts`'s `classifySkirmishChooser`.
 */
import { projectCombatantFor } from "@lancer-vtt/domain";
import type { MoveForecast } from "@lancer-vtt/rules";
import type {
  ActorRole,
  CombatantCurrent,
  CombatantId,
  DisclosureLevel,
  EncounterState,
  ProjectedCombatant,
  MaskedCombatant,
  SetDisclosureCommand,
  SetHpDisclosureCommand,
} from "@lancer-vtt/domain";

/**
 * Whether `c` is a masked (`"class"`/`"loadout"` tier) combatant — the render discriminant
 * `InspectorBody`/`NpcInspectorBody`/`tokenChromeFor` all branch on.
 *
 * **NOT `"disclosure" in c`.** `Combatant.disclosure` is an ordinary OPTIONAL field on the real,
 * unmasked `Combatant` type, so a genuinely full-disclosed, unredacted hostile (`disclosure:
 * "full"` stamped on the real combatant) satisfies `"disclosure" in x` too — testing the key's
 * mere PRESENCE collides the "masked-tier tag" reading with the ordinary bookkeeping field and
 * renders every explicitly-disclosed unit as masked, even at `"full"`. The reliable signal is
 * `current`'s ABSENCE: {@link MaskedCombatant} never carries `current` at all (the stat/loadout
 * AND current block are both gone), while `Combatant` and `HpHiddenCombatant` always do (HP-hiding
 * strips only `current.hp`, never `current` itself).
 */
export function isMaskedCombatant(c: ProjectedCombatant): c is MaskedCombatant {
  return !("current" in c);
}

/**
 * Whether an ALREADY-unmasked combatant's `current` carries a visible live HP value — false for a
 * `"full"`-disclosed hostile with `showEnemyHp` off (`current` is present but `hp` is stripped),
 * true otherwise (own unit, ally, GM view, or `showEnemyHp` on). Takes `current`
 * directly (not the whole combatant) — `MaskedCombatant` has no `current` at all, so a call site
 * must narrow past {@link isMaskedCombatant} first; a real type predicate (not a plain `boolean`)
 * so TS lets a subsequent `.hp` read through at the call site, which a plain-boolean helper
 * couldn't (TS doesn't correlate an opaque `boolean` back onto a property-existence fact).
 */
export function isHpVisible(current: CombatantCurrent | Omit<CombatantCurrent, "hp">): current is CombatantCurrent {
  return "hp" in current;
}

/**
 * Build the GM's manual disclosure-level command — the {@link DisclosureControl} 3-state segmented
 * control's dispatch. GM-only at the server gate (`canIssueEncounter`); undoable (the server's
 * inverse restores the prior level, including "never set").
 */
export function setDisclosureCommand(args: {
  actorId: string;
  combatantId: CombatantId;
  disclosure: DisclosureLevel;
}): SetDisclosureCommand {
  const { actorId, combatantId, disclosure } = args;
  return { type: "set_disclosure", actorId, combatantId, disclosure };
}

/**
 * Build the GM's per-encounter live-HP policy command — the TERRAIN window's "Disclosure" section
 * SHOW/HIDE toggle's dispatch. GM-only, undoable; encounter-level (no `combatantId` — it isn't
 * per-combatant).
 */
export function setHpDisclosureCommand(args: { actorId: string; showEnemyHp: boolean }): SetHpDisclosureCommand {
  const { actorId, showEnemyHp } = args;
  return { type: "set_hp_disclosure", actorId, showEnemyHp };
}

/**
 * The move forecast as one viewer may read it: the "could reach you here next turn" row
 * (`reachable_by`) keeps only the hostiles this viewer sees in full. Whether a unit appears in
 * that row is a function of its Speed and its longest weapon Range or Threat
 * (`couldReachNextTurn`), and dragging a ghost across the map reads it cell by cell, so naming a
 * masked hostile there hands a player the stat block its dossier shows as locked. Speed is a
 * stat-block value, so a `"loadout"`-tier hostile is dropped as well as a `"class"`-tier one. The
 * test is the render chokepoints' own ({@link projectCombatantFor} + {@link isMaskedCombatant}):
 * the GM, a full-view slot and allies keep every name. The row disappears when nothing is left.
 */
export function moveForecastFor(
  forecast: MoveForecast | null,
  viewer: { state: EncounterState; viewerSlot: string | null; role: ActorRole },
): MoveForecast | null {
  if (forecast === null || viewer.role === "gm") return forecast;
  const seen = (id: string): boolean => {
    const combatant = viewer.state.combatants[id];
    return combatant !== undefined && !isMaskedCombatant(projectCombatantFor({ combatant, viewerSlot: viewer.viewerSlot, role: viewer.role, state: viewer.state }));
  };
  const entries: MoveForecast["entries"] = [];
  for (const e of forecast.entries) {
    if (e.kind !== "reachable_by") {
      entries.push(e);
      continue;
    }
    const keep = e.unitIds.map((id, i) => [id, e.unitNames[i]!] as const).filter(([id]) => seen(id));
    if (keep.length > 0) entries.push({ kind: "reachable_by", unitIds: keep.map(([id]) => id), unitNames: keep.map(([, name]) => name) });
  }
  return { ...forecast, entries };
}
